// 로그인 이메일 칸 + **자동완성 목록**(2026-09-28 사용자 요청). 기억·추천 규칙은 `../loginEmails.ts`.
//
// - 치는 동안: 전에 로그인한 이메일 중 **친 글자로 시작하는 것**을 보여 준다. `@` 뒤를 치면 도메인을 제안한다.
// - 칸을 누르거나 ▾ 를 누르면(값이 비었거나 기억된 이메일 그대로일 때) 기억된 전부 — 계정 전환용.
//   ▾ 는 입력과 상관없이 **기억된 전부**를 연다.
// - 키보드: ↓/↑ 로 고르기 · Enter 로 넣기 · Esc 로 닫기(목록이 열려 있으면 로그인 팝업은 닫지 않는다).
// - 각 기억 항목의 × 로 목록에서 지운다(남의 PC 에서 로그인한 흔적을 지울 때).
// - 모양은 원격지 코드 칸(`RecentCodeField`)과 같은 CSS(`recent-code-*`)를 쓴다 — 이메일 글꼴만 `email-suggest` 로 덮는다.
import { useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { forgetLoginEmail, loadLoginEmails, suggestEmails } from '../loginEmails';
import type { EmailSuggestion } from '../loginEmails';

export function EmailSuggestField({
  value,
  onChange,
  onPicked,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  /** 목록에서 골랐다(비밀번호 칸으로 포커스를 넘기는 데 쓴다). */
  onPicked?: () => void;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [saved, setSaved] = useState<string[]>(() => loadLoginEmails());
  const [open, setOpen] = useState(false);
  // ▾ 로 열었으면 입력과 무관하게 기억된 전부.
  const [showAll, setShowAll] = useState(false);
  // 키보드로 고른 항목 위치(-1 = 없음).
  const [active, setActive] = useState(-1);

  const shown: EmailSuggestion[] = useMemo(
    () => (showAll ? saved.map((v) => ({ value: v, kind: 'saved' as const })) : suggestEmails(value, saved)),
    [showAll, saved, value],
  );
  const visible = open && shown.length > 0;

  const close = () => {
    setOpen(false);
    setShowAll(false);
    setActive(-1);
  };

  const pick = (v: string) => {
    onChange(v);
    close();
    onPicked?.();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!visible) {
        setOpen(true);
        setActive(0);
      } else {
        const d = e.key === 'ArrowDown' ? 1 : -1;
        setActive((i) => (i + d + shown.length) % shown.length);
      }
      e.preventDefault();
    } else if (e.key === 'Enter' && visible && active >= 0 && active < shown.length) {
      pick(shown[active].value);
      e.preventDefault();
    } else if (e.key === 'Escape' && visible) {
      close();
      e.stopPropagation(); // 로그인 팝업(바깥)의 Esc = 닫기 로 번지지 않게
    }
  };

  return (
    <div className="recent-code email-suggest">
      <div className="recent-code-row">
        <input
          ref={ref}
          type="email"
          inputMode="email"
          // 우리 목록과 브라우저 자동 채움 목록이 겹쳐 뜨지 않게 끈다.
          autoComplete="off"
          placeholder="name@example.com"
          aria-label="이메일"
          aria-autocomplete="list"
          aria-expanded={visible}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setShowAll(false);
            setActive(-1);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={close}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
        />
        {saved.length > 0 && (
          <button
            type="button"
            className="ghost recent-code-toggle"
            title="전에 로그인한 이메일"
            aria-label="전에 로그인한 이메일"
            // 누르는 순간 입력 칸이 blur 로 목록을 닫지 않게
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (visible && showAll) return close();
              // 포커스가 다른 칸(비밀번호)에 있으면 이메일 칸으로 가져온다 — 안 그러면 목록이 열린 채 ↓·Enter·Esc 가
              // 그 칸으로 가고, Esc 는 목록 대신 로그인 팝업을 닫는다(브라우저 시험에서 실측).
              ref.current?.focus();
              setShowAll(true);
              setActive(-1);
              setOpen(true);
            }}
          >
            ▾
          </button>
        )}
      </div>
      {visible && (
        <ul className="recent-code-list" role="listbox" aria-label="이메일 추천">
          {shown.map((s, i) => (
            <li key={s.value} role="option" aria-selected={i === active}>
              <button
                type="button"
                className="recent-code-item"
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => pick(s.value)}
              >
                <span className="recent-code-code">{s.value}</span>
              </button>
              {s.kind === 'saved' && (
                <button
                  type="button"
                  className="recent-code-del"
                  title="목록에서 지우기"
                  aria-label={`${s.value} 목록에서 지우기`}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => {
                    setSaved(forgetLoginEmail(s.value));
                    setActive(-1);
                  }}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
